import { useState } from 'react';
import { Wrench } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useServiceCatalog } from '@/hooks/useReferenceData';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { netFromGross, type TaxConfig } from '@/utils/tax';
import { round2 } from '@/utils/partsPricing';
import type { CatalogLineItem } from './AddCatalogPartButton';

interface AddCatalogServiceButtonProps {
  /** Tax config of the document — lines are stored ex-tax. */
  tax: TaxConfig;
  onAdd: (item: CatalogLineItem) => void;
}

/** Picker that appends a catalog service to an estimate or invoice at its default price.
 * A service with no default price goes on at 0 for the user to price on the line. */
export function AddCatalogServiceButton({ tax, onAdd }: AddCatalogServiceButtonProps) {
  const { data: catalog = [] } = useServiceCatalog();
  const { formatMoney } = useCompanyLocale();
  // Remount the Select after each pick so the placeholder comes back.
  const [pickKey, setPickKey] = useState(0);

  if (catalog.length === 0) return null;

  const handlePick = (id: string) => {
    const service = catalog.find(s => s.id === id);
    if (!service) return;
    const unit = round2(netFromGross(service.default_price ?? 0, tax));
    onAdd({ description: service.name, quantity: 1, unit_price: unit, amount: unit });
    setPickKey(k => k + 1);
  };

  return (
    <Select key={pickKey} onValueChange={handlePick}>
      <SelectTrigger className="w-[260px]" aria-label="Add service from catalog">
        <Wrench className="h-4 w-4 mr-2 text-muted-foreground" />
        <SelectValue placeholder="Add service from catalog" />
      </SelectTrigger>
      <SelectContent className="bg-popover z-50">
        {catalog.map(service => (
          <SelectItem key={service.id} value={service.id}>
            {service.name}
            {service.default_price != null ? ` — ${formatMoney(service.default_price)}` : ''}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
